/** Reconstruir o eixo de um pilar ou de uma viga a partir dos pontos
 *  que lhe calharam, e compará-lo com o eixo do projecto.
 *
 *  A distância ponto a ponto diz QUANTO a peça fugiu. Isto diz COMO:
 *  se o pilar está desaprumado, deslocado, ou se é a viga que está
 *  inclinada. É o que um fiscal de obra de estrutura metálica quer
 *  saber, e é o que o relatório de distâncias sozinho não dá.
 *
 *  Duas contas, as duas honestas com levantamentos incompletos (um
 *  laser de um lado só vê uma face do perfil):
 *
 *  - Rotação e fuga vêm de ajustar a peça medida à peça do projecto,
 *    face a face (ICP ponto-a-plano, ver `ajustarEixo`). NÃO do centro
 *    dos pontos: com uma face só vista, o centro dos pontos está na
 *    face e não no eixo, e dava um erro igual a meia largura do perfil.
 *
 *  - Se o que se viu não for comprido o bastante, não se diz nada.
 *
 *  O limite, que fica escrito: só se vê a fuga nas direcções em que há
 *  faces medidas. Um pilar visto só de sul não diz nada sobre
 *  nascente-poente. Por isso também se devolve em quantas direcções há
 *  faces vistas.
 */

export type Vec3 = [number, number, number]

/** A direcção principal de um conjunto de pontos (xyz seguidos), e
 *  quão alongado ele é. */
export interface Componentes {
  centro: Vec3
  direccao: Vec3
  /** Valores próprios, do maior para o menor. */
  valores: Vec3
}

export function componentesPrincipais(xyz: ArrayLike<number>): Componentes | null {
  const n = Math.floor(xyz.length / 3)
  if (n < 3) return null
  let cx = 0, cy = 0, cz = 0
  for (let i = 0; i < n; i += 1) {
    cx += xyz[i * 3]; cy += xyz[i * 3 + 1]; cz += xyz[i * 3 + 2]
  }
  cx /= n; cy /= n; cz /= n
  let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0
  for (let i = 0; i < n; i += 1) {
    const x = xyz[i * 3] - cx, y = xyz[i * 3 + 1] - cy, z = xyz[i * 3 + 2] - cz
    xx += x * x; xy += x * y; xz += x * z; yy += y * y; yz += y * z; zz += z * z
  }
  const m = [xx / n, xy / n, xz / n, xy / n, yy / n, yz / n, xz / n, yz / n, zz / n]
  const [valores, vectores] = jacobi(m)
  return { centro: [cx, cy, cz], direccao: vectores[0], valores }
}

/** Valores e vectores próprios de uma matriz simétrica 3x3 (Jacobi
 *  cíclico). Ordenados do maior valor para o menor. Com 3x3 converge
 *  em poucas voltas, e é exacto onde uma iteração de potência hesita
 *  (dois valores próprios parecidos, que é o caso de um pilar
 *  quadrado visto em corte). */
export function jacobi(m: number[]): [Vec3, [Vec3, Vec3, Vec3]] {
  const a = [...m]
  const v = [1, 0, 0, 0, 1, 0, 0, 0, 1]
  for (let volta = 0; volta < 50; volta += 1) {
    const fora = Math.abs(a[1]) + Math.abs(a[2]) + Math.abs(a[5])
    if (fora < 1e-15) break
    for (const [p, q] of [[0, 1], [0, 2], [1, 2]] as const) {
      const apq = a[p * 3 + q]
      if (Math.abs(apq) < 1e-18) continue
      const app = a[p * 3 + p], aqq = a[q * 3 + q]
      const theta = (aqq - app) / (2 * apq)
      const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1))
      const c = 1 / Math.sqrt(t * t + 1)
      const s = t * c
      for (let k = 0; k < 3; k += 1) {
        const akp = a[k * 3 + p], akq = a[k * 3 + q]
        a[k * 3 + p] = c * akp - s * akq
        a[k * 3 + q] = s * akp + c * akq
      }
      for (let k = 0; k < 3; k += 1) {
        const apk = a[p * 3 + k], aqk = a[q * 3 + k]
        a[p * 3 + k] = c * apk - s * aqk
        a[q * 3 + k] = s * apk + c * aqk
      }
      for (let k = 0; k < 3; k += 1) {
        const vkp = v[k * 3 + p], vkq = v[k * 3 + q]
        v[k * 3 + p] = c * vkp - s * vkq
        v[k * 3 + q] = s * vkp + c * vkq
      }
    }
  }
  const pares = [0, 1, 2]
    .map((i) => ({ valor: a[i * 4], vector: [v[i], v[3 + i], v[6 + i]] as Vec3 }))
    .sort((x, y) => y.valor - x.valor)
  return [
    [pares[0].valor, pares[1].valor, pares[2].valor],
    [pares[0].vector, pares[1].vector, pares[2].vector],
  ]
}

function norma(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2])
}

function escalar(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

/** Ângulo entre duas direcções, em graus, sem sentido (0 a 90). */
export function anguloEntre(a: Vec3, b: Vec3): number {
  const c = Math.abs(escalar(a, b)) / (norma(a) * norma(b))
  return (Math.acos(Math.min(1, c)) * 180) / Math.PI
}

/** O Y é o cimo, no viewer. */
export const CIMA: Vec3 = [0, 1, 0]

/** Abaixo disto, uma peça conta como vertical (pilar). Acima, como
 *  horizontal ou inclinada (viga, diagonal). */
export const LIMITE_VERTICAL_GRAUS = 20

/** Quanto mais comprido do que largo tem de ser o que se viu para a
 *  direcção querer dizer alguma coisa. Com uma nuvem redonda de pontos,
 *  a "direcção principal" é ruído. */
export const ALONGAMENTO_MIN = 9

export type Orientacao = 'vertical' | 'horizontal'

export interface Eixo {
  orientacao: Orientacao
  /** Direcção do projecto e direcção medida, unitárias. */
  projecto: Vec3
  medido: Vec3
  /** Ângulo entre as duas, em graus. */
  desvioAngular: number
  /** Só para verticais: desaprumo em relação ao eixo do projecto, em
   *  mm por metro de altura. */
  desaprumoMmPorM: number | null
  /** A fuga da peça, perpendicular ao eixo, em metros. Só tem
   *  componente nas direcções vistas. */
  deslocamento: Vec3
  deslocamentoTotal: number
  /** Quanto do comprimento da peça os pontos cobrem, em metros. */
  comprimentoVisto: number
  /** Em quantas das duas direcções perpendiculares ao eixo há faces
   *  medidas (0, 1 ou 2). Com 1, a fuga só se conhece nessa direcção:
   *  um pilar visto só de sul não diz nada sobre nascente-poente. */
  direccoesVistas: number
  /** Um desvio-padrão do desaprumo (ou da inclinação, numa viga), em
   *  mm/m, e da fuga, em metros. Vêm da qualidade do ajuste: poucos
   *  pontos, ruído, ou uma peça pequena dão incertezas grandes. */
  incertezaDesaprumoMmPorM: number
  incertezaFuga: number
}

export type SemEixo =
  | 'poucos-pontos'
  | 'projecto-sem-eixo'
  | 'pouco-alongado'

/** Para um ponto, o ponto da superfície do projecto mais próximo e a
 *  normal da face. Falso quando não há nenhum dentro do raio. */
export type Oraculo = (x: number, y: number, z: number, q: number[], normal: number[]) => boolean

/** Quantas voltas o ajuste dá, no máximo. Com fugas de alguns
 *  centímetros converge em quatro ou cinco. */
export const ITERACOES = 12

/**
 * Ajustar a peça medida à peça do projecto, e dizer quanto rodou e
 * quanto fugiu.
 *
 * É um ICP ponto-a-plano (Chen e Medioni, 1992; a linearização é a de
 * Low, 2004): em cada volta, cada ponto medido emparelha com o ponto
 * mais próximo da superfície do projecto, e procura-se a rotação e a
 * translação pequenas que encostam os pontos às faces, por mínimos
 * quadrados sobre as normais. Depois volta-se a emparelhar.
 *
 * Porquê iterar: a alma de um perfil em I tem 10 mm. Se o pilar fugiu
 * 15 mm, os pontos de uma face da alma ficam mais perto da OUTRA face,
 * e uma conta de uma volta só dizia 9,8 mm (medido no primeiro ensaio).
 * As faces largas puxam para o valor certo, a volta seguinte emparelha
 * melhor, e converge nos 15.
 *
 * Porquê a rotação no mesmo ajuste, e não a direcção dos pontos por
 * componentes principais: a PCA depende de como os pontos se espalham
 * pela secção, e num perfil em C ou em L esse espalhamento não é
 * simétrico. No ensaio, pilares a prumo davam até 2,4 mm/m de
 * desaprumo. O ajuste à geometria não tem esse viés.
 *
 * O que não se pode saber fica de fora: a translação ao longo do eixo
 * (um pilar visto só de lado não mostra a que altura está) e a rotação
 * em torno dele. Uma regularização pequena segura-as a zero.
 *
 * @param vertices vértices do elemento no projecto (xyz seguidos)
 * @param medidos  pontos medidos que lhe calharam
 * @param oraculo  o ponto mais próximo da superfície do projecto
 */
export function ajustarEixo(
  vertices: ArrayLike<number>,
  medidos: ArrayLike<number>,
  oraculo: Oraculo,
  iteracoes = ITERACOES,
): Eixo | SemEixo {
  const n = Math.floor(medidos.length / 3)
  if (n < 30) return 'poucos-pontos'
  const doProjecto = componentesPrincipais(vertices)
  if (!doProjecto || doProjecto.valores[0] < ALONGAMENTO_MIN * Math.max(doProjecto.valores[1], 1e-12)) {
    return 'projecto-sem-eixo'
  }
  const dosPontos = componentesPrincipais(medidos)
  if (!dosPontos || dosPontos.valores[0] < ALONGAMENTO_MIN * Math.max(dosPontos.valores[1], 1e-12)) {
    return 'pouco-alongado'
  }
  const eixo = doProjecto.direccao
  const c = doProjecto.centro
  const inclinacao = anguloEntre(eixo, CIMA)
  const orientacao: Orientacao = inclinacao <= LIMITE_VERTICAL_GRAUS ? 'vertical' : 'horizontal'

  // Pontos relativos ao centro do projecto: as contas ficam com números
  // pequenos e a rotação roda em torno do meio da peça.
  const p0 = new Float64Array(n * 3)
  let tMin = Infinity, tMax = -Infinity
  for (let i = 0; i < n; i += 1) {
    p0[i * 3] = medidos[i * 3] - c[0]
    p0[i * 3 + 1] = medidos[i * 3 + 1] - c[1]
    p0[i * 3 + 2] = medidos[i * 3 + 2] - c[2]
    const t = p0[i * 3] * eixo[0] + p0[i * 3 + 1] * eixo[1] + p0[i * 3 + 2] * eixo[2]
    if (t < tMin) tMin = t
    if (t > tMax) tMax = t
  }

  // A transformação que leva os pontos medidos para cima do projecto.
  let R = [1, 0, 0, 0, 1, 0, 0, 0, 1]
  let T: Vec3 = [0, 0, 0]
  const q = [0, 0, 0]
  const nr = [0, 0, 0]
  const [u, w] = baseOrtogonal(eixo)
  let suu = 0, suw = 0, sww = 0, comNormal = 0
  const linha = new Float64Array(6)
  // Da última volta: o sistema e os resíduos, para a incerteza.
  let ultimoA: Float64Array | null = null
  let somaR2 = 0
  let emparelhados = 0
  for (let volta = 0; volta < iteracoes; volta += 1) {
    const A = new Float64Array(36)
    const b = new Float64Array(6)
    suu = 0; suw = 0; sww = 0; comNormal = 0
    somaR2 = 0; emparelhados = 0
    for (let i = 0; i < n; i += 1) {
      const x0 = p0[i * 3], y0 = p0[i * 3 + 1], z0 = p0[i * 3 + 2]
      const px = R[0] * x0 + R[1] * y0 + R[2] * z0 + T[0]
      const py = R[3] * x0 + R[4] * y0 + R[5] * z0 + T[1]
      const pz = R[6] * x0 + R[7] * y0 + R[8] * z0 + T[2]
      if (!oraculo(px + c[0], py + c[1], pz + c[2], q, nr)) continue
      const qx = q[0] - c[0], qy = q[1] - c[1], qz = q[2] - c[2]
      // (p x n) . omega + n . t = n . (q - p)
      linha[0] = py * nr[2] - pz * nr[1]
      linha[1] = pz * nr[0] - px * nr[2]
      linha[2] = px * nr[1] - py * nr[0]
      linha[3] = nr[0]; linha[4] = nr[1]; linha[5] = nr[2]
      const r = nr[0] * (qx - px) + nr[1] * (qy - py) + nr[2] * (qz - pz)
      somaR2 += r * r
      emparelhados += 1
      for (let a = 0; a < 6; a += 1) {
        b[a] += linha[a] * r
        for (let k = 0; k < 6; k += 1) A[a * 6 + k] += linha[a] * linha[k]
      }
      // As normais laterais, para saber em que direcções a fuga se vê.
      const nu = nr[0] * u[0] + nr[1] * u[1] + nr[2] * u[2]
      const nw = nr[0] * w[0] + nr[1] * w[1] + nr[2] * w[2]
      const k = Math.hypot(nu, nw)
      // As tampas (topo e base de um pilar) têm a normal ao longo do
      // eixo e não dizem nada sobre a fuga lateral.
      if (k >= 0.5) {
        suu += (nu / k) ** 2; suw += (nu / k) * (nw / k); sww += (nw / k) ** 2
        comNormal += 1
      }
    }
    // Regularização pequena: o que não se vê fica a zero em vez de
    // tornar o sistema singular.
    let traco = 0
    for (let a = 0; a < 6; a += 1) traco += A[a * 7]
    const lambda = 1e-6 * (traco / 6) + 1e-12
    for (let a = 0; a < 6; a += 1) A[a * 7] += lambda
    ultimoA = A
    const x = resolver6(A, b)
    if (!x) break
    const Rinc = rodrigues([x[0], x[1], x[2]])
    R = multiplicar3(Rinc, R)
    T = [
      Rinc[0] * T[0] + Rinc[1] * T[1] + Rinc[2] * T[2] + x[3],
      Rinc[3] * T[0] + Rinc[4] * T[1] + Rinc[5] * T[2] + x[4],
      Rinc[6] * T[0] + Rinc[7] * T[1] + Rinc[8] * T[2] + x[5],
    ]
    // Pára quando a volta já só mexe micrómetros.
    if (Math.hypot(x[0], x[1], x[2]) < 1e-7 && Math.hypot(x[3], x[4], x[5]) < 1e-6) break
  }

  // A peça construída é a inversa: R transposta, e o centro foi parar a
  // -(R transposta) T.
  const medido: Vec3 = [
    R[0] * eixo[0] + R[3] * eixo[1] + R[6] * eixo[2],
    R[1] * eixo[0] + R[4] * eixo[1] + R[7] * eixo[2],
    R[2] * eixo[0] + R[5] * eixo[1] + R[8] * eixo[2],
  ]
  const centro: Vec3 = [
    -(R[0] * T[0] + R[3] * T[1] + R[6] * T[2]),
    -(R[1] * T[0] + R[4] * T[1] + R[7] * T[2]),
    -(R[2] * T[0] + R[5] * T[1] + R[8] * T[2]),
  ]
  let du = centro[0] * u[0] + centro[1] * u[1] + centro[2] * u[2]
  let dw = centro[0] * w[0] + centro[1] * w[1] + centro[2] * w[2]

  // Em que direcções há faces vistas: os valores próprios de S, por
  // ponto. Uma direcção conta quando pelo menos 10% das normais apontam
  // para ela.
  const m = comNormal || 1
  const a11 = suu / m, a12 = suw / m, a22 = sww / m
  const meio = (a11 + a22) / 2
  const raio = Math.sqrt(((a11 - a22) / 2) ** 2 + a12 * a12)
  const l1 = meio + raio
  const l2 = meio - raio
  const direccoesVistas = comNormal === 0 ? 0 : (l1 >= 0.1 ? 1 : 0) + (l2 >= 0.1 ? 1 : 0)
  if (direccoesVistas === 0) {
    du = 0
    dw = 0
  } else if (direccoesVistas === 1) {
    // Só a componente na direcção vista quer dizer alguma coisa.
    const ex = Math.abs(a12) > 1e-12 ? l1 - a22 : a11 >= a22 ? 1 : 0
    const ey = Math.abs(a12) > 1e-12 ? a12 : a11 >= a22 ? 0 : 1
    const ne = Math.hypot(ex, ey)
    const k = (du * ex + dw * ey) / (ne * ne)
    du = k * ex
    dw = k * ey
  }
  const deslocamento: Vec3 = [
    du * u[0] + dw * w[0],
    du * u[1] + dw * w[1],
    du * u[2] + dw * w[2],
  ]
  const desvioAngular = anguloEntre(eixo, medido)

  // Incerteza: covariância dos mínimos quadrados, sigma2 vezes a inversa
  // de A. Uma cantoneira de 64 mm com 150 pontos e 2 mm de ruído dá
  // desaprumos de 1 a 3 mm/m sem ter mexido; sem o desvio-padrão ao
  // lado, isso lia-se como desaprumo.
  let incertezaDesaprumoMmPorM = 0
  let incertezaFuga = 0
  if (ultimoA && emparelhados > 6) {
    const sigma2 = somaR2 / (emparelhados - 6)
    const coluna = (i: number) => {
      const e = new Float64Array(6)
      e[i] = 1
      return resolver6(ultimoA!, e)
    }
    const inv = [0, 1, 2, 3, 4, 5].map(coluna)
    if (inv.every(Boolean)) {
      const quad = (d: Vec3, off: number) => {
        let v = 0
        for (let i = 0; i < 3; i += 1)
          for (let j = 0; j < 3; j += 1) v += d[i] * inv[off + i]![off + j] * d[j]
        return Math.max(0, v) * sigma2
      }
      // As rotações que inclinam o eixo são as em torno de u e de w.
      const varRot = Math.max(quad(u, 0), quad(w, 0))
      incertezaDesaprumoMmPorM = Math.tan(Math.sqrt(varRot)) * 1000
      incertezaFuga = Math.sqrt(Math.max(quad(u, 3), quad(w, 3)))
    }
  }
  return {
    orientacao,
    projecto: eixo,
    medido,
    desvioAngular,
    // Em relação ao eixo do projecto e não ao vertical: um pilar
    // desenhado com um grau de inclinação não está desaprumado por
    // estar inclinado.
    desaprumoMmPorM: orientacao === 'vertical' ? Math.tan((desvioAngular * Math.PI) / 180) * 1000 : null,
    deslocamento,
    deslocamentoTotal: norma(deslocamento),
    comprimentoVisto: tMax - tMin,
    direccoesVistas,
    incertezaDesaprumoMmPorM,
    incertezaFuga,
  }
}

/** Rotação de um vector de rotação (eixo vezes ângulo), por Rodrigues.
 *  Matriz 3x3 por linhas. */
export function rodrigues(v: Vec3): number[] {
  const th = norma(v)
  if (th < 1e-15) return [1, 0, 0, 0, 1, 0, 0, 0, 1]
  const kx = v[0] / th, ky = v[1] / th, kz = v[2] / th
  const c = Math.cos(th), s = Math.sin(th), t = 1 - c
  return [
    c + kx * kx * t, kx * ky * t - kz * s, kx * kz * t + ky * s,
    ky * kx * t + kz * s, c + ky * ky * t, ky * kz * t - kx * s,
    kz * kx * t - ky * s, kz * ky * t + kx * s, c + kz * kz * t,
  ]
}

function multiplicar3(a: number[], b: number[]): number[] {
  const o = new Array<number>(9)
  for (let i = 0; i < 3; i += 1)
    for (let j = 0; j < 3; j += 1)
      o[i * 3 + j] = a[i * 3] * b[j] + a[i * 3 + 1] * b[3 + j] + a[i * 3 + 2] * b[6 + j]
  return o
}

/** Sistema 6x6 por eliminação de Gauss com pivô parcial. Null se for
 *  singular, o que com a regularização não deve acontecer. */
export function resolver6(A: Float64Array, b: Float64Array): number[] | null {
  const M: number[][] = []
  for (let i = 0; i < 6; i += 1) M.push([...A.slice(i * 6, i * 6 + 6), b[i]])
  for (let col = 0; col < 6; col += 1) {
    let piv = col
    for (let r = col + 1; r < 6; r += 1) if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r
    if (Math.abs(M[piv][col]) < 1e-300) return null
    const tmp = M[col]
    M[col] = M[piv]
    M[piv] = tmp
    for (let r = col + 1; r < 6; r += 1) {
      const f = M[r][col] / M[col][col]
      for (let k = col; k < 7; k += 1) M[r][k] -= f * M[col][k]
    }
  }
  const x = new Array<number>(6).fill(0)
  for (let r = 5; r >= 0; r -= 1) {
    let soma = M[r][6]
    for (let k = r + 1; k < 6; k += 1) soma -= M[r][k] * x[k]
    x[r] = soma / M[r][r]
  }
  return x
}

/** Dois vectores unitários perpendiculares a `d` e entre si. */
export function baseOrtogonal(d: Vec3): [Vec3, Vec3] {
  const ref: Vec3 = Math.abs(d[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]
  const u = cruz(d, ref)
  const nu = norma(u)
  const uu: Vec3 = [u[0] / nu, u[1] / nu, u[2] / nu]
  const w = cruz(d, uu)
  const nw = norma(w)
  return [uu, [w[0] / nw, w[1] / nw, w[2] / nw]]
}

function cruz(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}

/** O que dizer quando não há eixo. */
export function textoSemEixo(motivo: SemEixo): string {
  switch (motivo) {
    case 'poucos-pontos':
      return 'Pontos a menos para ajustar um eixo.'
    case 'projecto-sem-eixo':
      return 'No projecto, esta peça não é alongada: não tem eixo.'
    case 'pouco-alongado':
      return 'O que se mediu da peça é curto demais para dar uma direcção.'
  }
}
